import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { useAuth } from '../../contexts/AuthContext';
import { SignaturePad } from '../../components/SignaturePad';
import { saveSignature, getSignature, deleteSignature } from '../../services/api';
import { PencilLine, Trash } from '@phosphor-icons/react';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';

export function ProfileSettings() {
  const { user } = useAuth();
  const [signatureData, setSignatureData] = useState(null);
  const [signatureDate, setSignatureDate] = useState(null);
  const [showSignaturePad, setShowSignaturePad] = useState(false);
  const [loadingSig, setLoadingSig] = useState(true);

  useEffect(() => {
    const fetchSig = async () => {
      try {
        const res = await getSignature();
        setSignatureData(res.data.signature);
        setSignatureDate(res.data.updated_at);
      } catch {} finally { setLoadingSig(false); }
    };
    fetchSig();
  }, []);

  const handleSaveSignature = async (dataUrl) => {
    try {
      await saveSignature({ signature: dataUrl });
      setSignatureData(dataUrl);
      setSignatureDate(new Date().toISOString());
      setShowSignaturePad(false);
      toast.success('Signature enregistree');
    } catch { toast.error('Erreur sauvegarde signature'); }
  };

  const handleDeleteSignature = async () => {
    try {
      await deleteSignature();
      setSignatureData(null);
      setSignatureDate(null);
      toast.success('Signature supprimee');
    } catch { toast.error('Erreur suppression'); }
  };

  return (
    <div className="space-y-6">
      <Card className="border-[#E8E6E1]">
        <CardHeader>
          <CardTitle className="text-base" style={{ fontFamily: 'Manrope' }}>Profil</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Nom</Label>
              <Input value={user?.name || ''} disabled className="bg-[#F9F8F5]" />
            </div>
            <div className="space-y-2">
              <Label>Email</Label>
              <Input value={user?.email || ''} disabled className="bg-[#F9F8F5]" />
            </div>
          </div>
          <div className="space-y-2">
            <Label>Role</Label>
            <div><Badge className="bg-[#2D5A4C] text-white">{user?.role || '-'}</Badge></div>
          </div>
        </CardContent>
      </Card>

      <Card className="border-[#E8E6E1]">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-base flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
              <PencilLine size={20} className="text-[#2D5A4C]" />
              Signature electronique
            </CardTitle>
            {signatureData && !showSignaturePad && (
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => setShowSignaturePad(true)} data-testid="edit-signature-btn">
                  <PencilLine size={14} className="mr-1" /> Modifier
                </Button>
                <Button variant="ghost" size="sm" onClick={handleDeleteSignature} className="text-[#D47A62] hover:text-[#D47A62]" data-testid="delete-signature-btn">
                  <Trash size={14} className="mr-1" /> Supprimer
                </Button>
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {loadingSig ? (
            <div className="flex items-center justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-[#2D5A4C]" /></div>
          ) : showSignaturePad ? (
            <SignaturePad onSave={handleSaveSignature} onCancel={() => setShowSignaturePad(false)} />
          ) : signatureData ? (
            <div className="space-y-3">
              <div className="border border-[#E8E6E1] rounded-lg p-4 bg-white inline-block" data-testid="signature-preview">
                <img src={signatureData} alt="Votre signature" className="max-h-24" />
              </div>
              <p className="text-xs text-[#5E6D66]">
                Enregistree le {signatureDate ? new Date(signatureDate).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-'}
              </p>
              <p className="text-xs text-[#5E6D66]">
                Cette signature sera automatiquement apposee sur les devis et attestations PDF generes.
              </p>
            </div>
          ) : (
            <div className="text-center py-6">
              <PencilLine size={40} className="mx-auto text-[#E8E6E1] mb-3" />
              <p className="text-[#5E6D66] text-sm mb-3">Aucune signature enregistree</p>
              <Button onClick={() => setShowSignaturePad(true)} className="bg-[#2D5A4C] hover:bg-[#1E4136]" data-testid="create-signature-btn">
                <PencilLine size={16} className="mr-2" /> Creer ma signature
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
